import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import AttachmentIcon from '@mui/icons-material/Attachment';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import BalanceIcon from '@mui/icons-material/Balance';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import InsightsIcon from '@mui/icons-material/Insights';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import SatelliteAltIcon from '@mui/icons-material/SatelliteAlt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { buildTabMaps, SideTabSection } from '../../components/SideTabs';

export const DEFAULT_TAB_VALUE = '1';

/** Values only shown when the order actually uses that custodian. */
export const ALPACA_TAB_VALUE = '8';
export const JADWA_TAB_VALUE = '9';
export const ALBILAD_TAB_VALUE = '10';
export const DERAYAH_TAB_VALUE = '12';
export const SNB_TAB_VALUE = '13';

/**
 * Every tab, including the custodian ones that may be hidden. The `?tab=`
 * params are unchanged from the previous horizontal strip, and the lookup maps
 * are built from this full list so a link to a hidden tab still resolves.
 */
const ALL_SECTIONS: SideTabSection[] = [
  {
    title: 'Order',
    tabs: [
      { value: '1', param: 'portfolioOrder', label: 'Portfolio Order', IconComponent: AccountBoxIcon },
      { value: '2', param: 'fundOrders', label: 'Fund Orders', IconComponent: ListAltIcon },
      { value: '3', param: 'fundOrdersWeights', label: 'Weights', IconComponent: BalanceIcon },
      { value: '7', param: 'attachments', label: 'Attachments', IconComponent: AttachmentIcon },
    ],
  },
  {
    title: 'Requests',
    tabs: [
      { value: '4', param: 'subscriptionRequests', label: 'Subscription Requests', IconComponent: TrendingUpIcon },
      { value: '5', param: 'satelliteRequests', label: 'Satellite Requests', IconComponent: SatelliteAltIcon },
      { value: '6', param: 'consolidateRequests', label: 'Consolidate Requests', IconComponent: MergeTypeIcon },
    ],
  },
  {
    title: 'Custodians',
    tabs: [
      { value: ALPACA_TAB_VALUE, param: 'alpaca', label: 'Alpaca', IconComponent: InsightsIcon },
      { value: JADWA_TAB_VALUE, param: 'jadwa', label: 'Jadwa', IconComponent: AccountBalanceIcon },
      { value: ALBILAD_TAB_VALUE, param: 'albilad', label: 'Albilad', IconComponent: AccountBalanceWalletIcon },
      { value: DERAYAH_TAB_VALUE, param: 'derayah', label: 'Derayah', IconComponent: CurrencyExchangeIcon },
      { value: SNB_TAB_VALUE, param: 'snb', label: 'SNB', IconComponent: AccountBalanceIcon },
    ],
  },
  {
    title: 'Reconciliation',
    tabs: [{ value: '11', param: 'reconciliationRuns', label: 'Reconciliation Runs', IconComponent: AutoFixHighIcon }],
  },
];

export const {
  tabs: portfolioOrderTabs,
  mapTabParamToValue,
  mapTabValueToTabParam,
} = buildTabMaps(ALL_SECTIONS, DEFAULT_TAB_VALUE);

interface CustodianVisibility {
  showAlpacaTab: boolean;
  showJadwaTab: boolean;
  showAlbiladTab: boolean;
  showDerayahTab: boolean;
  showSnbTab: boolean;
}

/** Drops hidden custodian tabs, and any section left with nothing in it. */
export function getPortfolioOrderTabSections({
  showAlpacaTab,
  showJadwaTab,
  showAlbiladTab,
  showDerayahTab,
  showSnbTab,
}: CustodianVisibility): SideTabSection[] {
  const hidden = [
    !showAlpacaTab && ALPACA_TAB_VALUE,
    !showJadwaTab && JADWA_TAB_VALUE,
    !showAlbiladTab && ALBILAD_TAB_VALUE,
    !showDerayahTab && DERAYAH_TAB_VALUE,
    !showSnbTab && SNB_TAB_VALUE,
  ].filter(Boolean);

  return ALL_SECTIONS.map((section) => ({
    ...section,
    tabs: section.tabs.filter((tab) => !hidden.includes(tab.value)),
  })).filter((section) => section.tabs.length > 0);
}
